import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Modal,
  IconButton,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import WarningIcon from "@mui/icons-material/Warning";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  getFlaggedDetails,
  clearAllWafrQuestionnaireMessages,
} from "../../../store/wafrQuestionnaire/wafrQuestionnaireSlice";

const IssuesModal = ({ open, onClose, choiceId, choiceTitle, accNumber, automationState }) => {
  const dispatch = useDispatch();
  const { workloadId } = useParams();
  const [expandedIndex, setExpandedIndex] = useState(null);

  // Helper function to get state icon and tooltip
  const getStateIcon = (state) => {
    switch (state) {
      case 'PASS':
      case 'OKAY':
        return {
          icon: <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 20 }} />,
          tooltip: 'Pass - All checks completed successfully'
        };
      case 'ERROR':
        return {
          icon: <ErrorIcon sx={{ color: '#f44336', fontSize: 20 }} />,
          tooltip: 'Error - Issues detected that need attention'
        };
      case 'NOT_AVAILABLE':
        return {
          icon: <WarningIcon sx={{ color: '#ffb300', fontSize: 20 }} />,
          tooltip: 'Not Available - Check could not be performed'
        };
      default:
        return {
          icon: <WarningIcon sx={{ color: '#9e9e9e', fontSize: 20 }} />,
          tooltip: 'Unknown state'
        };
    }
  };

  // Access the flagged details state from Redux
  const { isGettingFlaggedDetails, getFlaggedDetailsError, flaggedDetails } =
    useSelector((state) => state.wafrQuestionnaire);

  // Convert flaggedDetails to an array if it's an object
  const detailsArray =
    flaggedDetails && !Array.isArray(flaggedDetails)
      ? Object.values(flaggedDetails)
      : flaggedDetails;

  // Sort detailsArray by status: ERROR, NOT_AVAILABLE, PARTIAL, OKAY/PASS
  const sortedDetailsArray = detailsArray && detailsArray.length > 0
    ? [...detailsArray].sort((a, b) => {
        const statusOrder = { ERROR: 0, NOT_AVAILABLE: 1, PARTIAL: 2, OKAY: 3, PASS: 3 };
        const aOrder = statusOrder[a.status] ?? 99;
        const bOrder = statusOrder[b.status] ?? 99;
        return aOrder - bOrder;
      })
    : detailsArray;

  // Fetch flagged details when the modal opens
  useEffect(() => {
    if (open && choiceId && accNumber && workloadId) {
      dispatch(
        getFlaggedDetails({ accountNumber: accNumber, choiceId, workloadId })
      );
    }
  }, [open, choiceId, accNumber, workloadId, dispatch]);

  // Clear messages and close modal
  const handleClose = () => {
    dispatch(clearAllWafrQuestionnaireMessages());
    onClose();
  };

  // Toggle expand/collapse for rows
  const toggleExpand = (index) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="issues-modal-title"
      aria-describedby="issues-modal-description"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "80%",
          maxHeight: "90%",
          bgcolor: "background.paper",
          boxShadow: 24,
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            backgroundColor: "#F5F5F5",
            padding: "16px 24px",
            borderBottom: "1px solid #E0E0E0",
          }}
        >
          <Typography
            id="issues-modal-title"
            variant="h6"
            fontWeight="bold"
            sx={{
              maxWidth: "90%",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {choiceTitle}
          </Typography>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider />

        {/* Content */}
        <Box
          sx={{ padding: "16px 24px", overflowY: "auto", maxHeight: "80vh" }}
        >
          {isGettingFlaggedDetails ? (
            <Typography>Loading...</Typography>
          ) : getFlaggedDetailsError ? (
            <Typography color="error">
              Error: {getFlaggedDetailsError}
            </Typography>
          ) : sortedDetailsArray && sortedDetailsArray.length > 0 ? (
            sortedDetailsArray.map((detail, index) => (
              <Box key={index} sx={{ marginBottom: "24px" }}>
                {/* Title */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    cursor: "pointer",
                    backgroundColor: "#F5F5F5",
                    padding: "12px 16px",
                    borderRadius: "8px",
                    gap: 2,
                  }}
                  onClick={() => toggleExpand(index)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {/* Status Icon */}
                    {detail.status && (
                      <Tooltip title={getStateIcon(detail.status).tooltip} arrow>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {getStateIcon(detail.status).icon}
                        </Box>
                      </Tooltip>
                    )}
                    <Typography variant="body1" fontWeight="bold">
                      {detail.name}
                    </Typography>
                    {/* Inline divider and status info */}
                    {detail.resourceDetails &&
                      (detail.resourceDetails.filter(r => r.Status === 'Red').length > 0 ||
                        detail.resourceDetails.filter(r => r.Status === 'Yellow').length > 0) && (
                        <>
                          <Typography variant="body2" color="text.disabled" sx={{ mx: 1 }}>|</Typography>
                          <Typography variant="caption" color="text.primary" fontWeight={600}>
                            Action needed on Resources:
                          </Typography>
                          {/* Red dot and count */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
                            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#f44336', border: '1px solid #e57373' }} />
                            <Typography variant="caption" color="text.primary" fontWeight={600}>
                              {detail.resourceDetails.filter(r => r.Status === 'Red').length}
                            </Typography>
                          </Box>
                          {/* Yellow dot and count */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
                            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#ffb300', border: '1px solid #ffe082' }} />
                            <Typography variant="caption" color="text.primary" fontWeight={600}>
                              {detail.resourceDetails.filter(r => r.Status === 'Yellow').length}
                            </Typography>
                          </Box>
                        </>
                      )}
                  </Box>
                  {expandedIndex === index ? (
                    <ExpandLessIcon />
                  ) : (
                    <ExpandMoreIcon />
                  )}
                </Box>

                {/* Description and DataGrid */}
                <Collapse
                  in={expandedIndex === index}
                  timeout="auto"
                  unmountOnExit
                >
                  <Box sx={{ marginTop: "16px" }}>
                    {/* Description Box */}
                    <Box
                      sx={{
                        backgroundColor: "#FAFAFA",
                        padding: "16px",
                        borderRadius: "8px",
                        marginBottom: "16px",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          "& h4": { margin: "16px 0", fontWeight: "bold" },
                          "& a": {
                            color: "#1976d2",
                            textDecoration: "none",
                            "&:hover": { textDecoration: "underline" },
                          },
                          lineHeight: 1.6,
                        }}
                        dangerouslySetInnerHTML={{ __html: detail.description }}
                      />
                    </Box>

                    {/* DataGrid Box */}
                    <Box
                      sx={{
                        backgroundColor: "#FFFFFF",
                        padding: "16px",
                        borderRadius: "8px",
                        boxShadow: "0 0 10px rgba(0,0,0,0.1)",
                      }}
                    >
                      {detail.resourceDetails &&
                      detail.resourceDetails.length > 0 ? (
                        <TableContainer
                          sx={{
                            maxHeight: "40vh", // Limit the height of the scrollable table area
                            overflowY: "auto", // Allow vertical scrolling
                            borderRadius: "8px",
                            backgroundColor: "#FFFFFF",
                          }}
                        >
                          <Table stickyHeader>
                            <TableHead>
                              <TableRow>
                                {Object.keys(detail.resourceDetails[0])
                                  .filter((key) => key !== "Status")
                                  .map((key) => (
                                    <TableCell
                                      key={key}
                                      sx={{
                                        fontWeight: "bold",
                                        position: "sticky",
                                        top: 0,
                                        backgroundColor:
                                          "rgba(245, 245, 245, 0.95)",
                                        color: "#000000",
                                        zIndex: 10,
                                        borderBottom: "2px solid #E0E0E0",
                                        padding: "8px 16px",
                                        boxShadow:
                                          "0 2px 2px rgba(0, 0, 0, 0.1)",
                                        backdropFilter: "blur(5px)", // Optional
                                      }}
                                    >
                                      {key}
                                    </TableCell>
                                  ))}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {(detail.resourceDetails
                                ? [...detail.resourceDetails].sort((a, b) => {
                                    const order = { Red: 0, Yellow: 1, Green: 2 };
                                    return (order[a.Status] ?? 99) - (order[b.Status] ?? 99);
                                  })
                                : []
                              ).map((row, rowIndex) => (
                                <TableRow key={rowIndex}>
                                  {Object.keys(row)
                                    .filter((key) => key !== "Status")
                                    .map((key) => (
                                      <TableCell
                                        key={key}
                                        sx={{
                                          padding: "8px 16px", // Consistent padding for table rows
                                        }}
                                      >
                                        {row[key]}
                                      </TableCell>
                                    ))}
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      ) : (
                        <>
                          {detail.status === "PASS" || detail.status === "OKAY" ? (
                            <Typography>
                              All resources are in compliance with this check.
                            </Typography>
                          ) : detail.status === "NOT_AVAILABLE" ? (
                            <Typography>
                              Some automated checks need manual validation due to inconsistent results
                            </Typography>
                          ) : null}
                        </>
                      )}
                    </Box>
                  </Box>
                </Collapse>
              </Box>
            ))
          ) : (
            <Typography>All the scanned resources are in compliance.</Typography>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default IssuesModal;
